// cspell:ignore sints
import { cryptoRandom } from "@redotech/crypto-util/random";
import { Brand, Schema } from "effect";

// Named types for ID schemas - prevents TypeScript from expanding full structural
// types in declaration files, significantly reducing .d.ts size and compile time.
// See: https://github.com/microsoft/TypeScript/wiki/Performance#preferring-interfaces-over-intersections

export type PrefixedBigIntId<T extends string> = string & Brand.Brand<T>;

export type PrefixedBigIntIdFromDb<T extends string> = Schema.transform<
  Schema.transform<
    Schema.Union<[typeof Schema.BigIntFromSelf, typeof Schema.String]>,
    typeof Schema.BigIntFromSelf
  >,
  Schema.brand<Schema.filter<typeof Schema.String>, T>
>;

export interface PrefixedBigIntIdSchema<T extends string>
  extends Schema.brand<Schema.filter<typeof Schema.String>, T> {
  readonly make: (id: string) => PrefixedBigIntId<T>;
  readonly generate: () => PrefixedBigIntId<T>;
  readonly fromBigInt: (id: bigint) => PrefixedBigIntId<T>;
  readonly toBigInt: (id: PrefixedBigIntId<T>) => bigint;
  readonly fromNumber: (n: number) => PrefixedBigIntId<T>;
  readonly fromDb: PrefixedBigIntIdFromDb<T>;
  readonly prefix: string;
}

export const generateRandomBigInt = (): bigint => {
  const bytes = new Uint8Array(cryptoRandom.bytes(8));
  let result = 0n;
  for (let i = 0; i < 8; i++) {
    result = (result << 8n) | BigInt(bytes[i]!);
  }
  return result & 0x7f_ff_ff_ff_ff_ff_ff_ffn;
};

export const IdPrefix = {
  Product: "prd",
  ProductFamily: "pfam",
  Conversation: "cnv",
  SupportTicketInsights: "cins",
  ConversationPrice: "cprc",
  ConversationExpense: "cexp",
  ConversationTimelineEvent: "ctle",
  VoiceParticipant: "cprt",
  RingingCall: "recall",
  VoiceQueue: "vq",
  VoiceUserAvailability: "vua",
  Account: "ac",
  Team: "m_team",
  Organization: "org",
  ShopperAiTagGroup: "tgr",
  ShopperAiTag: "ctag",
  UserGroup: "ugrp",
  ShopperAiIntegration: "sint",
  ShopperAiIntegrationSetting: "sints",
  ChannelThread: "cthr",
  ChannelThreadAnalytics: "ctha",
  ChannelThreadEmail: "cthe",
  ChannelThreadChatFlow: "cthf",
  // cspell:disable-next-line
  ChannelThreadFacebookComment: "cthfb",
  // cspell:disable-next-line
  ChannelThreadInstagramComment: "cthig",
  ChannelThreadMetaComment: "cthm",
  ChannelThreadOrders: "ctho",
  ThreadProductAnalytics: "tpa",
  ThreadToolAnalytics: "tta",
  Message: "msg",
  MessageAttachment: "matt",
  DraftMessage: "dmsg",
  DraftMessageError: "dmer",
  MessageEmail: "meml",
  MessageGmail: "mgml",
  MessageOutlook: "mout",
  MessageEmailAddress: "mema",
  MessageFacebookComment: "mfb",
  MessageInstagramComment: "mig",
  MessageInternalNote: "mint",
  MessageContactForm: "mcf",
  MessageMetaComment: "mmc",
  MessageRedoChat: "mrc",
  MessageSms: "msms",
  MessageVoice: "mvce",
  ShopperAiAttentiveIntegration: "satt",
  ShopperAiFacebookIntegration: "sfb",
  ShopperAiGmailIntegration: "sgml",
  ShopperAiInstagramIntegration: "sig",
  ShopperAiOutlookIntegration: "sout",
  ShopperAiPostscriptIntegration: "sps",
  ShopperAiPostscriptWebhook: "spsw",
  ShopperAiContactFormIntegration: "scf",
  ShopperAiImessageIntegration: "imsg",
  ShopperAiSmsPhoneNumberIntegration: "sspn",
  ShopperAiTelnyxSmsIntegration: "stlx",
  // cspell:disable-next-line
  ShopperAiTelnyxVoiceIntegration: "stlxv",
  ShopperAiTiktokIntegration: "stik",
  TimeBasedIntegrationSync: "tbis",
  // cspell:disable-next-line
  TimeBasedIntegrationSyncMessage: "tbism",
  IntegrationTagMapping: "itm",
  Macro: "mcr",
  SupportV2MigrationRun: "sv2mr",
  SupportV2MigrationStep: "sv2ms",
  SupportV2MigrationFailure: "sv2mf",
  ViewFolder: "vfld",
  ChannelThreadSms: "cths",
  ChannelThreadVoice: "cthv",
  // cspell:disable-next-line
  ChannelThreadMobileMessage: "cthmm",
  ChannelThreadProductSupport: "ctps",
  ChannelThreadProductShopperAi: "ctpa",
  MessageProductSupport: "mpsu",
  MessageProductShopperAi: "mpsa",
  View: "view",
  // cspell:disable-next-line
  CustomerActionAuthorization: "cauth",
  IMessagePhoneCapability: "impc",
  // cspell:disable-next-line
  IMessageChannelExperimentAssignment: "imcea",
  // cspell:disable-next-line
  IMessageChannelExperimentEvent: "imcev",
} as const;
type IdPrefix = (typeof IdPrefix)[keyof typeof IdPrefix];

const prefixes = Object.values(IdPrefix);
const prefixSet = new Set(prefixes);
if (prefixes.length !== prefixSet.size) {
  throw new Error("IdPrefix values must be unique");
}

const BASE62_ALPHABET =
  "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz";
const BASE = 62n;

const BASE62_LOOKUP = new Map(
  [...BASE62_ALPHABET].map((char, i) => [char, BigInt(i)]),
);

function encodeBase62(num: bigint): string {
  if (num === 0n) {
    return "0";
  }
  const chars: string[] = [];
  let n = num;
  while (n > 0n) {
    const val = BASE62_ALPHABET[Number(n % BASE)]!;
    chars.push(val);
    n = n / BASE;
  }
  return chars.reverse().join("");
}

function decodeBase62(str: string): bigint {
  let result = 0n;
  for (const char of str) {
    const value = BASE62_LOOKUP.get(char);
    if (value === undefined) {
      throw new Error(`Invalid base62 character: ${char}`);
    }
    result = result * BASE + value;
  }
  return result;
}

const BigIntFromDb = Schema.transform(
  Schema.Union(Schema.BigIntFromSelf, Schema.String),
  Schema.BigIntFromSelf,
  {
    strict: true,
    decode: (value) => (typeof value === "string" ? BigInt(value) : value),
    encode: (value) => value,
  },
);

export function createPrefixedBigIntIdSchema<T extends string>(
  typeName: T,
  prefix: string,
): PrefixedBigIntIdSchema<T> {
  const regex = new RegExp(`^${prefix}_[${BASE62_ALPHABET}]+$`);

  const baseSchema = Schema.String.pipe(
    Schema.pattern(regex, {
      message: () => `${typeName} must match ${prefix}_<base62>`,
    }),
    Schema.brand(typeName),
  );

  type IdType = PrefixedBigIntId<T>;

  const make = (id: string): IdType =>
    Schema.decodeSync(baseSchema)(id) as IdType;

  const fromBigInt = (id: bigint): IdType =>
    make(`${prefix}_${encodeBase62(id)}`);

  const toBigInt = (id: IdType): bigint =>
    decodeBase62(id.slice(prefix.length + 1));

  const fromNumber = (n: number): IdType => fromBigInt(BigInt(n));

  const fromDb: PrefixedBigIntIdFromDb<T> = Schema.transform(
    BigIntFromDb,
    baseSchema,
    {
      strict: true,
      decode: (bigint): IdType => fromBigInt(bigint),
      encode: (id): bigint => toBigInt(id as IdType),
    },
  ) as PrefixedBigIntIdFromDb<T>;

  const generate = (): IdType => fromBigInt(generateRandomBigInt());

  return Object.assign(baseSchema, {
    make,
    generate,
    fromBigInt,
    toBigInt,
    fromNumber,
    fromDb,
    prefix,
  }) as PrefixedBigIntIdSchema<T>;
}

export const ProductId = createPrefixedBigIntIdSchema(
  "Product",
  IdPrefix.Product,
);
export type ProductId = typeof ProductId.Type;

export const ProductFamilyId = createPrefixedBigIntIdSchema(
  "ProductFamily",
  IdPrefix.ProductFamily,
);
export type ProductFamilyId = typeof ProductFamilyId.Type;

export const TopicId = createPrefixedBigIntIdSchema(
  "Topic",
  IdPrefix.Conversation,
);
export type TopicId = typeof TopicId.Type;

// Legacy code still uses `ConversationId` for the generic topic row.
// New support-ticket APIs should use `SupportTicketId` at product boundaries.
export const ConversationId = TopicId;
export type ConversationId = TopicId;

export const SupportTicketId = createPrefixedBigIntIdSchema(
  "SupportTicket",
  IdPrefix.Conversation,
);
export type SupportTicketId = typeof SupportTicketId.Type;

export const supportTicketIdToTopicId = (id: SupportTicketId): TopicId =>
  TopicId.make(id);

export const topicIdToSupportTicketId = (id: TopicId): SupportTicketId =>
  SupportTicketId.make(id);

export const TextConciergeConversationId = createPrefixedBigIntIdSchema(
  "TextConciergeConversation",
  IdPrefix.Conversation,
);
export type TextConciergeConversationId =
  typeof TextConciergeConversationId.Type;

export const textConciergeConversationIdToTopicId = (
  id: TextConciergeConversationId,
): TopicId => TopicId.make(id);

export const topicIdToTextConciergeConversationId = (
  id: TopicId,
): TextConciergeConversationId => TextConciergeConversationId.make(id);

export const SupportTicketInsightsId = createPrefixedBigIntIdSchema(
  "SupportTicketInsights",
  IdPrefix.SupportTicketInsights,
);
export type SupportTicketInsightsId = typeof SupportTicketInsightsId.Type;

export const ConversationPriceId = createPrefixedBigIntIdSchema(
  "ConversationPrice",
  IdPrefix.ConversationPrice,
);
export type ConversationPriceId = typeof ConversationPriceId.Type;

export const ConversationExpenseId = createPrefixedBigIntIdSchema(
  "ConversationExpense",
  IdPrefix.ConversationExpense,
);
export type ConversationExpenseId = typeof ConversationExpenseId.Type;

export const ConversationTimelineEventId = createPrefixedBigIntIdSchema(
  "ConversationTimelineEvent",
  IdPrefix.ConversationTimelineEvent,
);
export type ConversationTimelineEventId =
  typeof ConversationTimelineEventId.Type;

export const VoiceParticipantId = createPrefixedBigIntIdSchema(
  "VoiceParticipant",
  IdPrefix.VoiceParticipant,
);
export type VoiceParticipantId = typeof VoiceParticipantId.Type;

export const RingingCallId = createPrefixedBigIntIdSchema(
  "RingingCall",
  IdPrefix.RingingCall,
);
export type RingingCallId = typeof RingingCallId.Type;

export const VoiceQueueId = createPrefixedBigIntIdSchema(
  "VoiceQueue",
  IdPrefix.VoiceQueue,
);
export type VoiceQueueId = typeof VoiceQueueId.Type;

export const VoiceUserAvailabilityId = createPrefixedBigIntIdSchema(
  "VoiceUserAvailability",
  IdPrefix.VoiceUserAvailability,
);
export type VoiceUserAvailabilityId = typeof VoiceUserAvailabilityId.Type;

export const AccountId = createPrefixedBigIntIdSchema(
  "Account",
  IdPrefix.Account,
);
export type AccountId = typeof AccountId.Type;

export const TeamId = createPrefixedBigIntIdSchema("Team", IdPrefix.Team);
export type TeamId = typeof TeamId.Type;

export const OrganizationId = createPrefixedBigIntIdSchema(
  "Organization",
  IdPrefix.Organization,
);
export type OrganizationId = typeof OrganizationId.Type;

export const ShopperAiTagGroupId = createPrefixedBigIntIdSchema(
  "ShopperAiTagGroup",
  IdPrefix.ShopperAiTagGroup,
);
export type ShopperAiTagGroupId = typeof ShopperAiTagGroupId.Type;

export const ShopperAiTagId = createPrefixedBigIntIdSchema(
  "ShopperAiTag",
  IdPrefix.ShopperAiTag,
);
export type ShopperAiTagId = typeof ShopperAiTagId.Type;

export const UserGroupId = createPrefixedBigIntIdSchema(
  "UserGroup",
  IdPrefix.UserGroup,
);
export type UserGroupId = typeof UserGroupId.Type;

export const ShopperAiIntegrationId = createPrefixedBigIntIdSchema(
  "ShopperAiIntegration",
  IdPrefix.ShopperAiIntegration,
);
export type ShopperAiIntegrationId = typeof ShopperAiIntegrationId.Type;

export const ShopperAiIntegrationSettingId = createPrefixedBigIntIdSchema(
  "ShopperAiIntegrationSetting",
  IdPrefix.ShopperAiIntegrationSetting,
);
export type ShopperAiIntegrationSettingId =
  typeof ShopperAiIntegrationSettingId.Type;

export const ChannelThreadId = createPrefixedBigIntIdSchema(
  "ChannelThread",
  IdPrefix.ChannelThread,
);
export type ChannelThreadId = typeof ChannelThreadId.Type;

export const ChannelThreadAnalyticsId = createPrefixedBigIntIdSchema(
  "ChannelThreadAnalytics",
  IdPrefix.ChannelThreadAnalytics,
);
export type ChannelThreadAnalyticsId = typeof ChannelThreadAnalyticsId.Type;

export const ChannelThreadEmailId = createPrefixedBigIntIdSchema(
  "ChannelThreadEmail",
  IdPrefix.ChannelThreadEmail,
);
export type ChannelThreadEmailId = typeof ChannelThreadEmailId.Type;

export const ChannelThreadChatFlowId = createPrefixedBigIntIdSchema(
  "ChannelThreadChatFlow",
  IdPrefix.ChannelThreadChatFlow,
);
export type ChannelThreadChatFlowId = typeof ChannelThreadChatFlowId.Type;

export const ChannelThreadFacebookCommentId = createPrefixedBigIntIdSchema(
  "ChannelThreadFacebookComment",
  IdPrefix.ChannelThreadFacebookComment,
);
export type ChannelThreadFacebookCommentId =
  typeof ChannelThreadFacebookCommentId.Type;

export const ChannelThreadInstagramCommentId = createPrefixedBigIntIdSchema(
  "ChannelThreadInstagramComment",
  IdPrefix.ChannelThreadInstagramComment,
);
export type ChannelThreadInstagramCommentId =
  typeof ChannelThreadInstagramCommentId.Type;

export const ChannelThreadMetaCommentId = createPrefixedBigIntIdSchema(
  "ChannelThreadMetaComment",
  IdPrefix.ChannelThreadMetaComment,
);
export type ChannelThreadMetaCommentId = typeof ChannelThreadMetaCommentId.Type;

export const ChannelThreadOrdersId = createPrefixedBigIntIdSchema(
  "ChannelThreadOrders",
  IdPrefix.ChannelThreadOrders,
);
export type ChannelThreadOrdersId = typeof ChannelThreadOrdersId.Type;

export const ThreadProductAnalyticsId = createPrefixedBigIntIdSchema(
  "ThreadProductAnalytics",
  IdPrefix.ThreadProductAnalytics,
);
export type ThreadProductAnalyticsId = typeof ThreadProductAnalyticsId.Type;

export const ThreadToolAnalyticsId = createPrefixedBigIntIdSchema(
  "ThreadToolAnalytics",
  IdPrefix.ThreadToolAnalytics,
);
export type ThreadToolAnalyticsId = typeof ThreadToolAnalyticsId.Type;

export const MessageId = createPrefixedBigIntIdSchema(
  "Message",
  IdPrefix.Message,
);
export type MessageId = typeof MessageId.Type;

export const MessageAttachmentId = createPrefixedBigIntIdSchema(
  "MessageAttachment",
  IdPrefix.MessageAttachment,
);
export type MessageAttachmentId = typeof MessageAttachmentId.Type;

export const DraftMessageId = createPrefixedBigIntIdSchema(
  "DraftMessage",
  IdPrefix.DraftMessage,
);
export type DraftMessageId = typeof DraftMessageId.Type;

export const DraftMessageErrorId = createPrefixedBigIntIdSchema(
  "DraftMessageError",
  IdPrefix.DraftMessageError,
);
export type DraftMessageErrorId = typeof DraftMessageErrorId.Type;

export const MessageEmailId = createPrefixedBigIntIdSchema(
  "MessageEmail",
  IdPrefix.MessageEmail,
);
export type MessageEmailId = typeof MessageEmailId.Type;

export const MessageGmailId = createPrefixedBigIntIdSchema(
  "MessageGmail",
  IdPrefix.MessageGmail,
);
export type MessageGmailId = typeof MessageGmailId.Type;

export const MessageOutlookId = createPrefixedBigIntIdSchema(
  "MessageOutlook",
  IdPrefix.MessageOutlook,
);
export type MessageOutlookId = typeof MessageOutlookId.Type;

export const MessageEmailAddressId = createPrefixedBigIntIdSchema(
  "MessageEmailAddress",
  IdPrefix.MessageEmailAddress,
);
export type MessageEmailAddressId = typeof MessageEmailAddressId.Type;

export const MessageFacebookCommentId = createPrefixedBigIntIdSchema(
  "MessageFacebookComment",
  IdPrefix.MessageFacebookComment,
);
export type MessageFacebookCommentId = typeof MessageFacebookCommentId.Type;

export const MessageInstagramCommentId = createPrefixedBigIntIdSchema(
  "MessageInstagramComment",
  IdPrefix.MessageInstagramComment,
);
export type MessageInstagramCommentId = typeof MessageInstagramCommentId.Type;

export const MessageInternalNoteId = createPrefixedBigIntIdSchema(
  "MessageInternalNote",
  IdPrefix.MessageInternalNote,
);
export type MessageInternalNoteId = typeof MessageInternalNoteId.Type;

export const MessageContactFormId = createPrefixedBigIntIdSchema(
  "MessageContactForm",
  IdPrefix.MessageContactForm,
);
export type MessageContactFormId = typeof MessageContactFormId.Type;

export const MessageMetaCommentId = createPrefixedBigIntIdSchema(
  "MessageMetaComment",
  IdPrefix.MessageMetaComment,
);
export type MessageMetaCommentId = typeof MessageMetaCommentId.Type;

export const MessageRedoChatId = createPrefixedBigIntIdSchema(
  "MessageRedoChat",
  IdPrefix.MessageRedoChat,
);
export type MessageRedoChatId = typeof MessageRedoChatId.Type;

export const MessageSmsId = createPrefixedBigIntIdSchema(
  "MessageSms",
  IdPrefix.MessageSms,
);
export type MessageSmsId = typeof MessageSmsId.Type;

export const MessageVoiceId = createPrefixedBigIntIdSchema(
  "MessageVoice",
  IdPrefix.MessageVoice,
);
export type MessageVoiceId = typeof MessageVoiceId.Type;

export const ShopperAiAttentiveIntegrationId = createPrefixedBigIntIdSchema(
  "ShopperAiAttentiveIntegration",
  IdPrefix.ShopperAiAttentiveIntegration,
);
export type ShopperAiAttentiveIntegrationId =
  typeof ShopperAiAttentiveIntegrationId.Type;

export const ShopperAiFacebookIntegrationId = createPrefixedBigIntIdSchema(
  "ShopperAiFacebookIntegration",
  IdPrefix.ShopperAiFacebookIntegration,
);
export type ShopperAiFacebookIntegrationId =
  typeof ShopperAiFacebookIntegrationId.Type;

export const ShopperAiGmailIntegrationId = createPrefixedBigIntIdSchema(
  "ShopperAiGmailIntegration",
  IdPrefix.ShopperAiGmailIntegration,
);
export type ShopperAiGmailIntegrationId =
  typeof ShopperAiGmailIntegrationId.Type;

export const ShopperAiInstagramIntegrationId = createPrefixedBigIntIdSchema(
  "ShopperAiInstagramIntegration",
  IdPrefix.ShopperAiInstagramIntegration,
);
export type ShopperAiInstagramIntegrationId =
  typeof ShopperAiInstagramIntegrationId.Type;

export const ShopperAiOutlookIntegrationId = createPrefixedBigIntIdSchema(
  "ShopperAiOutlookIntegration",
  IdPrefix.ShopperAiOutlookIntegration,
);
export type ShopperAiOutlookIntegrationId =
  typeof ShopperAiOutlookIntegrationId.Type;

export const ShopperAiPostscriptIntegrationId = createPrefixedBigIntIdSchema(
  "ShopperAiPostscriptIntegration",
  IdPrefix.ShopperAiPostscriptIntegration,
);
export type ShopperAiPostscriptIntegrationId =
  typeof ShopperAiPostscriptIntegrationId.Type;

export const ShopperAiPostscriptWebhookId = createPrefixedBigIntIdSchema(
  "ShopperAiPostscriptWebhook",
  IdPrefix.ShopperAiPostscriptWebhook,
);
export type ShopperAiPostscriptWebhookId =
  typeof ShopperAiPostscriptWebhookId.Type;

export const ShopperAiContactFormIntegrationId = createPrefixedBigIntIdSchema(
  "ShopperAiContactFormIntegration",
  IdPrefix.ShopperAiContactFormIntegration,
);
export type ShopperAiContactFormIntegrationId =
  typeof ShopperAiContactFormIntegrationId.Type;

export const ShopperAiImessageIntegrationId = createPrefixedBigIntIdSchema(
  "ShopperAiImessageIntegration",
  IdPrefix.ShopperAiImessageIntegration,
);
export type ShopperAiImessageIntegrationId =
  typeof ShopperAiImessageIntegrationId.Type;

export const ShopperAiSmsPhoneNumberIntegrationId =
  createPrefixedBigIntIdSchema(
    "ShopperAiSmsPhoneNumberIntegration",
    IdPrefix.ShopperAiSmsPhoneNumberIntegration,
  );
export type ShopperAiSmsPhoneNumberIntegrationId =
  typeof ShopperAiSmsPhoneNumberIntegrationId.Type;

export const ShopperAiTelnyxSmsIntegrationId = createPrefixedBigIntIdSchema(
  "ShopperAiTelnyxSmsIntegration",
  IdPrefix.ShopperAiTelnyxSmsIntegration,
);
export type ShopperAiTelnyxSmsIntegrationId =
  typeof ShopperAiTelnyxSmsIntegrationId.Type;

export const ShopperAiTelnyxVoiceIntegrationId = createPrefixedBigIntIdSchema(
  "ShopperAiTelnyxVoiceIntegration",
  IdPrefix.ShopperAiTelnyxVoiceIntegration,
);
export type ShopperAiTelnyxVoiceIntegrationId =
  typeof ShopperAiTelnyxVoiceIntegrationId.Type;

export const ShopperAiTiktokIntegrationId = createPrefixedBigIntIdSchema(
  "ShopperAiTiktokIntegration",
  IdPrefix.ShopperAiTiktokIntegration,
);
export type ShopperAiTiktokIntegrationId =
  typeof ShopperAiTiktokIntegrationId.Type;

export const TimeBasedIntegrationSyncId = createPrefixedBigIntIdSchema(
  "TimeBasedIntegrationSync",
  IdPrefix.TimeBasedIntegrationSync,
);
export type TimeBasedIntegrationSyncId = typeof TimeBasedIntegrationSyncId.Type;

export const TimeBasedIntegrationSyncMessageId = createPrefixedBigIntIdSchema(
  "TimeBasedIntegrationSyncMessage",
  IdPrefix.TimeBasedIntegrationSyncMessage,
);
export type TimeBasedIntegrationSyncMessageId =
  typeof TimeBasedIntegrationSyncMessageId.Type;

export const IntegrationTagMappingId = createPrefixedBigIntIdSchema(
  "IntegrationTagMapping",
  IdPrefix.IntegrationTagMapping,
);
export type IntegrationTagMappingId = typeof IntegrationTagMappingId.Type;

export const MacroId = createPrefixedBigIntIdSchema("Macro", IdPrefix.Macro);
export type MacroId = typeof MacroId.Type;

export const SupportV2MigrationRunId = createPrefixedBigIntIdSchema(
  "SupportV2MigrationRun",
  IdPrefix.SupportV2MigrationRun,
);
export type SupportV2MigrationRunId = typeof SupportV2MigrationRunId.Type;

export const SupportV2MigrationStepId = createPrefixedBigIntIdSchema(
  "SupportV2MigrationStep",
  IdPrefix.SupportV2MigrationStep,
);
export type SupportV2MigrationStepId = typeof SupportV2MigrationStepId.Type;

export const SupportV2MigrationFailureId = createPrefixedBigIntIdSchema(
  "SupportV2MigrationFailure",
  IdPrefix.SupportV2MigrationFailure,
);
export type SupportV2MigrationFailureId =
  typeof SupportV2MigrationFailureId.Type;

export const ViewFolderId = createPrefixedBigIntIdSchema(
  "ViewFolder",
  IdPrefix.ViewFolder,
);
export type ViewFolderId = typeof ViewFolderId.Type;

export const ChannelThreadSmsId = createPrefixedBigIntIdSchema(
  "ChannelThreadSms",
  IdPrefix.ChannelThreadSms,
);
export type ChannelThreadSmsId = typeof ChannelThreadSmsId.Type;

export const ChannelThreadVoiceId = createPrefixedBigIntIdSchema(
  "ChannelThreadVoice",
  IdPrefix.ChannelThreadVoice,
);
export type ChannelThreadVoiceId = typeof ChannelThreadVoiceId.Type;

export const ChannelThreadMobileMessageId = createPrefixedBigIntIdSchema(
  "ChannelThreadMobileMessage",
  IdPrefix.ChannelThreadMobileMessage,
);
export type ChannelThreadMobileMessageId =
  typeof ChannelThreadMobileMessageId.Type;

export const ChannelThreadProductSupportId = createPrefixedBigIntIdSchema(
  "ChannelThreadProductSupport",
  IdPrefix.ChannelThreadProductSupport,
);
export type ChannelThreadProductSupportId =
  typeof ChannelThreadProductSupportId.Type;

export const ChannelThreadProductShopperAiId = createPrefixedBigIntIdSchema(
  "ChannelThreadProductShopperAi",
  IdPrefix.ChannelThreadProductShopperAi,
);
export type ChannelThreadProductShopperAiId =
  typeof ChannelThreadProductShopperAiId.Type;

export const MessageProductSupportId = createPrefixedBigIntIdSchema(
  "MessageProductSupport",
  IdPrefix.MessageProductSupport,
);
export type MessageProductSupportId = typeof MessageProductSupportId.Type;

export const MessageProductShopperAiId = createPrefixedBigIntIdSchema(
  "MessageProductShopperAi",
  IdPrefix.MessageProductShopperAi,
);
export type MessageProductShopperAiId = typeof MessageProductShopperAiId.Type;

export const ViewId = createPrefixedBigIntIdSchema("View", IdPrefix.View);
export type ViewId = typeof ViewId.Type;

export const CustomerActionAuthorizationId = createPrefixedBigIntIdSchema(
  "CustomerActionAuthorization",
  IdPrefix.CustomerActionAuthorization,
);
export type CustomerActionAuthorizationId =
  typeof CustomerActionAuthorizationId.Type;

export const IMessagePhoneCapabilityId = createPrefixedBigIntIdSchema(
  "IMessagePhoneCapability",
  IdPrefix.IMessagePhoneCapability,
);
export type IMessagePhoneCapabilityId = typeof IMessagePhoneCapabilityId.Type;

export const IMessageChannelExperimentAssignmentId =
  createPrefixedBigIntIdSchema(
    "IMessageChannelExperimentAssignment",
    IdPrefix.IMessageChannelExperimentAssignment,
  );
export type IMessageChannelExperimentAssignmentId =
  typeof IMessageChannelExperimentAssignmentId.Type;

export const IMessageChannelExperimentEventId = createPrefixedBigIntIdSchema(
  "IMessageChannelExperimentEvent",
  IdPrefix.IMessageChannelExperimentEvent,
);
export type IMessageChannelExperimentEventId =
  typeof IMessageChannelExperimentEventId.Type;
